import type { FC, ReactNode } from 'react'
import type Lenis from 'lenis'

import { useRouter } from 'next/router'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'
import { holdStage, isStageReady, releaseStage } from '@/lib/stage'
import { useLenis } from '@/components/motion/smooth-scroll'
import { CortexMark } from '@/components/generals/logo'

interface TransitionProviderProps {
    children: ReactNode
}

interface TransitionContextValue {
    navigate: (href: string) => void
}

const TransitionContext = createContext<TransitionContextValue>({
    navigate: () => undefined
})

export const usePageTransition = (): TransitionContextValue => useContext(TransitionContext)

const PAGE_LABELS: Record<string, string> = {
    '/': 'Home',
    '/about': 'About',
    '/solutions': 'Solutions',
    '/services': 'Services',
    '/case-studies': 'Case Studies',
    '/contact': 'Contact'
}

const HEADER_OFFSET = 88

const labelFor = (pathname: string): string => {
    if (PAGE_LABELS[pathname]) return PAGE_LABELS[pathname]
    if (pathname.startsWith('/case-studies/')) return 'Case Study'
    return 'Cortex'
}

const scrollToHash = (lenis: Lenis | null, hash: string, immediate: boolean): void => {
    let target: Element | null = null

    try {
        target = document.querySelector(decodeURIComponent(hash))
    }
    catch {
        target = null
    }

    if (!(target instanceof HTMLElement)) return

    if (lenis) {
        lenis.scrollTo(target, { offset: -HEADER_OFFSET, immediate, force: true })
        return
    }

    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
    window.scrollTo({ top, behavior: immediate ? 'auto' : 'smooth' })
}

export const TransitionProvider: FC<TransitionProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const lenisRef = useRef<Lenis | null>(lenis)
    const curtainRef = useRef<HTMLDivElement>(null)
    const preloaderRef = useRef<HTMLDivElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)
    const barRef = useRef<HTMLSpanElement>(null)
    const busyRef = useRef(false)
    const pendingHashRef = useRef<string | null>(null)
    const [label, setLabel] = useState('Cortex')
    const [preloaderDone, setPreloaderDone] = useState(false)

    useEffect(() => {
        lenisRef.current = lenis
    }, [lenis])

    // Intro screen on the first full load
    useEffect(() => {
        const preloader = preloaderRef.current

        if (!preloader) return

        if (isStageReady()) {
            setPreloaderDone(true)
            return
        }

        if (prefersReducedMotion()) {
            releaseStage()
            gsap.to(preloader, {
                autoAlpha: 0,
                duration: 0.35,
                onComplete: () => setPreloaderDone(true)
            })
            return
        }

        const counter = { value: 0 }
        const timeline = gsap.timeline()

        timeline
            .from(preloader.querySelectorAll('[data-preloader-item]'), {
                yPercent: 120,
                autoAlpha: 0,
                duration: 0.9,
                stagger: 0.08,
                ease: 'expo.out'
            })
            .to(counter, {
                value: 100,
                duration: 1.25,
                ease: 'power2.inOut',
                onUpdate: () => {
                    if (counterRef.current) {
                        counterRef.current.textContent = String(Math.round(counter.value)).padStart(3, '0')
                    }
                }
            }, 0.1)
            .to(barRef.current, {
                scaleX: 1,
                duration: 1.25,
                ease: 'power2.inOut'
            }, 0.1)
            .to(preloader.querySelectorAll('[data-preloader-item]'), {
                yPercent: -120,
                autoAlpha: 0,
                duration: 0.5,
                stagger: 0.04,
                ease: 'power3.in'
            }, '+=0.1')
            .add(() => releaseStage())
            .to(preloader, {
                yPercent: -100,
                duration: 1.1,
                ease: 'expo.inOut'
            }, '<')
            .add(() => setPreloaderDone(true))

        return () => {
            timeline.kill()
        }
    }, [])

    const navigate = useCallback((href: string) => {
        const url = new URL(href, window.location.href)

        if (url.origin !== window.location.origin) {
            window.location.href = href
            return
        }

        if (url.pathname === window.location.pathname) {
            if (url.hash) {
                scrollToHash(lenisRef.current, url.hash, false)
                return
            }

            if (lenisRef.current) lenisRef.current.scrollTo(0)
            else window.scrollTo({ top: 0, behavior: 'smooth' })

            return
        }

        if (busyRef.current) return

        busyRef.current = true
        pendingHashRef.current = url.hash || null
        holdStage()

        const curtain = curtainRef.current
        const destination = `${url.pathname}${url.search}`

        if (!curtain || prefersReducedMotion()) {
            void router.push(destination, undefined, { scroll: false })
            return
        }

        setLabel(labelFor(url.pathname))
        lenisRef.current?.stop()

        const items = curtain.querySelectorAll('[data-curtain-item]')

        gsap.timeline()
            .set(curtain, { visibility: 'visible', yPercent: 100 })
            .set(items, { yPercent: 120, autoAlpha: 0 })
            .to(curtain, { yPercent: 0, duration: 0.8, ease: 'expo.inOut' })
            .to(items, {
                yPercent: 0,
                autoAlpha: 1,
                duration: 0.6,
                stagger: 0.06,
                ease: 'expo.out'
            }, '-=0.35')
            .add(() => {
                void router.push(destination, undefined, { scroll: false })
            })
    }, [router])

    useEffect(() => {
        const lift = (): void => {
            const curtain = curtainRef.current
            const hash = pendingHashRef.current
            const wasBusy = busyRef.current

            pendingHashRef.current = null

            window.scrollTo(0, 0)
            lenisRef.current?.scrollTo(0, { immediate: true, force: true })
            lenisRef.current?.start()

            requestAnimationFrame(() => {
                ScrollTrigger.refresh()

                if (hash) scrollToHash(lenisRef.current, hash, true)

                if (!wasBusy || !curtain || prefersReducedMotion()) {
                    if (curtain) gsap.set(curtain, { visibility: 'hidden' })
                    releaseStage()
                    busyRef.current = false
                    return
                }

                const items = curtain.querySelectorAll('[data-curtain-item]')

                gsap.timeline({
                    onComplete: () => {
                        gsap.set(curtain, { visibility: 'hidden' })
                        busyRef.current = false
                    }
                })
                    .to(items, {
                        yPercent: -120,
                        autoAlpha: 0,
                        duration: 0.45,
                        stagger: 0.04,
                        ease: 'power3.in'
                    }, 0.1)
                    .add(() => releaseStage())
                    .to(curtain, {
                        yPercent: -100,
                        duration: 0.95,
                        ease: 'expo.inOut'
                    }, '<')
            })
        }

        const fail = (): void => {
            const curtain = curtainRef.current

            lenisRef.current?.start()
            if (curtain) gsap.set(curtain, { visibility: 'hidden' })
            releaseStage()
            busyRef.current = false
        }

        router.events.on('routeChangeComplete', lift)
        router.events.on('routeChangeError', fail)

        return () => {
            router.events.off('routeChangeComplete', lift)
            router.events.off('routeChangeError', fail)
        }
    }, [router])

    const contextValue = useMemo(() => ({ navigate }), [navigate])

    return (
        <TransitionContext.Provider value={contextValue}>
            {children}

            {/* Page transition curtain */}
            <div
                ref={curtainRef}
                aria-hidden='true'
                className='invisible fixed inset-0 z-[90] flex flex-col justify-between bg-surface-2 px-5 py-6 md:px-8 lg:px-10'
            >
                <div className='flex items-center justify-between overflow-hidden'>
                    <span data-curtain-item className='type-label text-muted-foreground'>
                        Cortex / Intelligent systems
                    </span>

                    <span data-curtain-item className='type-label text-muted-foreground'>
                        Loading
                    </span>
                </div>

                <div className='flex items-end justify-between gap-6 overflow-hidden pb-2'>
                    <span data-curtain-item className='type-display text-foreground'>
                        {label}
                    </span>

                    <span data-curtain-item className='mb-3 hidden text-ember md:block'>
                        <CortexMark className='size-14' />
                    </span>
                </div>
            </div>

            {/* Intro screen, first load only */}
            {!preloaderDone && (
                <div
                    ref={preloaderRef}
                    aria-hidden='true'
                    className='preloader-failsafe fixed inset-0 z-[100] flex flex-col justify-between bg-background px-5 py-6 md:px-8 lg:px-10'
                >
                    <div className='flex items-center justify-between overflow-hidden'>
                        <span data-preloader-item className='type-label text-muted-foreground'>
                            Cortex / AI automation
                        </span>

                        <span data-preloader-item className='type-label text-muted-foreground'>
                            Intelligent systems
                        </span>
                    </div>

                    <div className='flex flex-col items-center gap-6 overflow-hidden'>
                        <span data-preloader-item className='text-foreground'>
                            <CortexMark className='size-16 md:size-20' />
                        </span>

                        <span data-preloader-item className='font-heading text-4xl font-medium tracking-[-0.05em] md:text-5xl'>
                            Cortex
                        </span>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <div className='flex items-end justify-between overflow-hidden'>
                            <span data-preloader-item className='type-label text-muted-foreground'>
                                Initializing systems
                            </span>

                            <span data-preloader-item className='font-mono text-sm text-foreground tabular-nums'>
                                <span ref={counterRef}>000</span>
                            </span>
                        </div>

                        <span className='relative block h-px w-full bg-line'>
                            <span
                                ref={barRef}
                                className='absolute inset-0 origin-left scale-x-0 bg-ember'
                            />
                        </span>
                    </div>
                </div>
            )}
        </TransitionContext.Provider>
    )
}
